import React, { useContext, useMemo, useState } from 'react'
import { SharedCampaignContext } from '../../../../../contexts/Advertiser/SharedCampaignContext'
import ShowAll from './ImportantMetricsParts/ShowAll'
import ProviderMetrics from './ImportantMetricsParts/ProviderMetrics'
import { InfoCircle } from 'react-bootstrap-icons'
import { separateThousands, currencyFormat } from '../../../../../utils/Helper'

export default function ImportantMetrics({ campaignReport }) {
  const { selectedScenario, campaign, influencers } = useContext(SharedCampaignContext)
  const [selected, setSelected] = useState('show_all')

  const providers = useMemo(() => selectedScenario?.actions?.reduce((result, action) => {
    if(!result.includes(action.provider)) result.push(action.provider)
    return result
  }, []) || [], [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])

  const totalInteractions = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.action_insight?.clicks || 0) + (action?.action_insight?.comments || 0) + (action?.action_insight?.likes || 0) + (action?.action_insight?.saves || 0) + (action?.action_insight?.shares || 0) + (action?.action_insight?.views || 0) }, 0) }, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const CampaignEngagementRate = useMemo(() => { return (totalInteractions / campaignReport.gross_reach) * 100 }, [totalInteractions, campaignReport.gross_reach])
  const campaignClicks = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.action_insight?.clicks || 0) }, 0) }, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const campaignImpressions = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.action_insight?.impressions || action?.action_insight?.story_first_sequence_impressions || 0) }, 0)}, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const ClickThroughRate = useMemo(() => { return (campaignClicks / campaignImpressions) * 100 }, [campaignClicks, campaignImpressions])
  const totalSpent = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.advertiserPrice || 0) }, 0) }, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const CPM = useMemo(() => { return (totalSpent / campaignImpressions) * 1000 }, [totalSpent, campaignImpressions])
  const CPE = useMemo(() => { return totalSpent / totalInteractions }, [totalSpent, totalInteractions])

  const ImportantMetric = ({title, metric, info}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    return (
      <div className='border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 flex flex-col justify-between relative'>
        <div className="flex items-center justify-between mb-8">
          <span className="font-bold text-sm dark:text-white">
            {title}
          </span>
          <div
            className='text-rb-green text-sm relative'
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <InfoCircle />
            {showTooltip && info && (
              <div className="absolute -top-8 left-full ml-1 z-10 bg-rb-light-green dark:bg-rb-dark-mode-lighter p-2 rounded-md w-[200px] border-1 border-rb-green text-sm font-bold dark:text-white">
                {info}
              </div>
            )}
          </div>
        </div>
        <span className="bg-rb-grey dark:bg-rb-dark-mode-lighter p-2 rounded-sm w-fit font-bold text-lg dark:text-white">
          {metric}
        </span>
      </div>
    );
  }

  return (
    <div className='w-full h-full bg-white dark:bg-rb-dark-mode border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4'>
      <h1 className='font-bold text-xl dark:text-white'>Important Metrics</h1>
      <div className='flex items-center border-b-1 border-rb-widget-border dark:border-rb-dark-mode-lighter gap-2 mt-1'>
        <button type='button' className={selected === 'show_all' ? 'text-rb-green dark:text-rb-green border-b-1 border-rb-green' : 'dark:text-white'} onClick={() => setSelected('show_all')}>Show all</button>
        {providers.includes('instagram') && <button type='button' className={selected === 'instagram' ? 'text-rb-green dark:text-rb-green border-b-1 border-rb-green' : 'dark:text-white'} onClick={() => setSelected('instagram')}>Instagram</button>}
        {providers.includes('tiktok') && <button type='button' className={selected === 'tiktok' ? 'text-rb-green dark:text-rb-green border-b-1 border-rb-green' : 'dark:text-white'} onClick={() => setSelected('tiktok')}>Tiktok</button>}
        {providers.includes('youtube') && <button type='button' className={selected === 'youtube' ? 'text-rb-green dark:text-rb-green border-b-1 border-rb-green' : 'dark:text-white'} onClick={() => setSelected('youtube')}>Youtube</button>}
      </div>

      <div className="mt-8">
        <p className='font-bold text-sm mb-2 dark:text-white'>Overview Goals / Actual</p>
      </div>
      {
        selected === 'show_all' ? (
          <ShowAll selectedScenario={selectedScenario} />
        ) : (
          <ProviderMetrics provider={selected} selectedScenario={selectedScenario} influencers={influencers} />
        )
      }

      <h1 className='font-bold text-lg my-4 dark:text-white'>Overview - Paid</h1>
      <table className='w-full overflow-hidden bg-white dark:bg-rb-dark-mode border-separate border-spacing-0 border-t border-r border-l border-b border-border-primary dark:border-rb-dark-mode-lighter'>
        <tbody>
          <tr>
            <td className="text-sm font-bold p-2 border border-border-primary dark:border-rb-dark-mode-lighter w-1/3 dark:text-white">Total Impressions - Paid</td>
            <td className="border border-border-primary dark:border-rb-dark-mode-lighter p-2 align-middle dark:text-white">{separateThousands(campaignReport.paid_impressions)}</td>
          </tr>
          <tr>
            <td className="text-sm font-bold p-2 border border-border-primary dark:border-rb-dark-mode-lighter w-1/3 dark:text-white">Total Reach - Paid</td>
            <td className="border border-border-primary dark:border-rb-dark-mode-lighter p-2 align-middle dark:text-white">{separateThousands(campaignReport.paid_reach)}</td>
          </tr>
          <tr>
            <td className="text-sm font-bold p-2 border border-border-primary dark:border-rb-dark-mode-lighter w-1/3 dark:text-white">Cost per Click (CPC) - Paid</td>
            <td className="border border-border-primary dark:border-rb-dark-mode-lighter p-2 align-middle dark:text-white">{currencyFormat((campaignReport.paid_spend_overview / campaignReport.paid_clicks).toFixed(2), campaign.currency)}</td>
          </tr>
          <tr>
            <td className="text-sm font-bold p-2 border border-border-primary dark:border-rb-dark-mode-lighter w-1/3 dark:text-white">Cost per Thousand (CPM) - Paid</td>
            <td className="border border-border-primary dark:border-rb-dark-mode-lighter p-2 align-middle dark:text-white">{currencyFormat(((campaignReport.paid_spend_overview / campaignReport.paid_impressions) * 1000).toFixed(2), campaign.currency)}</td>
          </tr>

          {campaignReport.paid_roi_visible && (
            <tr>
              <td className="text-sm font-bold p-2 border border-border-primary dark:border-rb-dark-mode-lighter w-1/3 dark:text-white"> Return on Investment (ROI) - Paid</td>
              <td className="border border-border-primary dark:border-rb-dark-mode-lighter p-2 align-middle dark:text-white">{currencyFormat(((campaignReport.paid_revenue_generated / campaignReport.paid_spend_overview) * 100).toFixed(2), campaign.currency)}</td>
            </tr>
          )}
          <tr>
            <td className="text-sm font-bold p-2 border border-border-primary dark:border-rb-dark-mode-lighter w-1/3 dark:text-white">Ad Budget</td>
            <td className="border border-border-primary dark:border-rb-dark-mode-lighter p-2 align-middle dark:text-white">{currencyFormat(campaignReport.paid_spend_overview, campaign.currency)}</td>
          </tr>
          <tr>
            <td className="text-sm font-bold p-2 border border-border-primary dark:border-rb-dark-mode-lighter w-1/3 dark:text-white">Comment</td>
            <td className="border border-border-primary dark:border-rb-dark-mode-lighter p-2 align-middle dark:text-white">{campaignReport.paid_comment}</td>
          </tr>
        </tbody>
      </table>

      <div className="pt-8 grid grid-cols-2 gap-2">
        <ImportantMetric 
          title="Campaign Gross Reach" 
          metric={separateThousands(campaignReport.gross_reach)}
          info="Gross Reach = Influencer 1 (Follower count * Actions) + Influencer 2 (Follower count * Actions) + ..." 
        />
        <ImportantMetric 
          title="Total Interactions" 
          metric={separateThousands(totalInteractions)} 
          info="Σ Interactions (Like + View + Comment + Shares + Saves)" 
        />
        <ImportantMetric 
          title="Campaign Engagement Rate" 
          metric={`${CampaignEngagementRate.toFixed(2)} %`} 
          info="Engagement Rate = (Total Interactions / Total Reach) * 100" 
        />
        <ImportantMetric 
          title="Click-Through Rate (CTR) " 
          metric={`${ClickThroughRate.toFixed(2)} %`} 
          info="CTR = (Total Clicks / Total Impressions) * 100" 
        />
        <ImportantMetric 
          title="Cost per Mille (CPM)" 
          metric={currencyFormat(CPM.toFixed(2), campaign.currency)} 
          info="CPM = (Total Spend / Total Impressions) * 1000" 
        />
        <ImportantMetric 
          title="Cost per Engagement (CPE)" 
          metric={currencyFormat(CPE.toFixed(2), campaign.currency)} 
          info="CPE = Total Spend / Total Engagements" 
        />
        {campaignReport.conversion_rate_visible && ( 
          <ImportantMetric 
            title="Conversion Rate (CR)" 
            metric={((campaignReport.conversions / campaignClicks) * 100).toFixed(2)}
            info="CR = (Total Conversions / Total Clicks) * 100" 
          />
        )}
        {campaignReport.roi_visible && (
          <ImportantMetric
            title="Return on Investment (ROI)" 
            metric={currencyFormat(((campaignReport.revenue_generated / totalSpent) * 100).toFixed(2), campaign.currency)} 
            info="ROI = (Revenue Generated / Total Spent) * 100" 
          />
        )}
      </div>
    </div>
  )
}
